<template>
  <div :class="{'print-body':!preview}">
    <DefaultNoticeOfViolation
      v-if="template === 'default'"
      :model="model"
      :voluntary="options.voluntary"
      @ready="doPrint"
    />
    <OOBNoticeOfCompliance
      v-if="template === 'oob'"
      :model="model"
      :type="options.type"
      :destination="options.destination"
      @ready="doPrint"
    />
    <SoPoLetter
      v-if="template === 'sopo'"
      :model="model"
      :first="options.first"
      @ready="doPrint"
    />
    <FalmouthLetter
      v-if="template === 'falmouth'"
      :model="model"
      :first="options.first"
      v-on="$attrs"
      @ready="doPrint"
    />
    <FreeportLetter
      v-if="template === 'freeport'"
      :model="model"
      :first="options.first"
      v-on="$attrs"
      @ready="doPrint"
    />
    <PortlandLetter
      v-if="template === 'portland'"
      :model="model"
      :first="options.first"
      :registered="options.registered"
      v-on="$attrs"
      @ready="doPrint"
    />
    <CapeElizabethLetter
      v-if="template === 'cape_elizabeth'"
      :model="model"
      @ready="doPrint"
    />
    <CascoLetter
      v-if="template === 'casco'"
      :model="model"
      @ready="doPrint"
    />
    <CascoViolationLetter
      v-if="template === 'casco_violation'"
      :model="model"
      @ready="doPrint"
    />
    <EvidenceAttachment
      v-if="includeScreenshot"
      ref="evidenceAttachment"
      :city-id="model?.cityId"
      :listing-id="model?.listingId"
      :evidence="model?.evidence"
      :source-url="model?.evidence?.sourceUrl"
      :captured-at="model?.evidence?.capturedAt"
    />
  </div>
</template>

<script>
import DefaultNoticeOfViolation from "@/components/letters/DefaultLetter.vue";
import OOBNoticeOfCompliance from "@/components/letters/OOBLetter.vue";
import SoPoLetter from "@/components/letters/SoPoLetter.vue";
import FalmouthLetter from "@/components/letters/FalmouthLetter.vue";
import PortlandLetter from "@/components/letters/PortlandLetter.vue";
import EvidenceAttachment from "@/components/letters/EvidenceAttachment.vue";
import {LetterContext} from "@/models/letterContext";
import CapeElizabethLetter from "@/components/letters/CapeElizabethLetter.vue";
import FreeportLetter from "@/components/letters/FreeportLetter.vue";
import CascoLetter from "@/components/letters/CascoLetter.vue";
import CascoViolationLetter from "@/components/letters/CascoViolationLetter.vue";

export default {
  components: {
    CascoViolationLetter,
    CascoLetter,
    FreeportLetter,
    CapeElizabethLetter,
    EvidenceAttachment, PortlandLetter, FalmouthLetter, SoPoLetter, OOBNoticeOfCompliance, DefaultNoticeOfViolation},
  props: {
    model: LetterContext,
    preview: Boolean
  },
  emits: [
    'printed'
  ],
  data() {
    return {
      template: null,
      options: {},
      includeScreenshot: false,
      onPrinted: null,
      onFailed: null,
      printVersion: 0,
      printStarted: false,
      printTimer: null
    };
  },
  watch: {
    model() {
      this.cancelPrint();
    }
  },
  beforeUnmount() {
    this.cancelPrint();
  },
  methods: {
    /** Remove temporary print content and invalidate callbacks from an earlier print attempt. */
    resetPrint() {
      clearTimeout(this.printTimer);
      this.printVersion += 1;
      this.template = null;
      this.options = {};
      this.includeScreenshot = false;
      this.printStarted = false;
      this.onPrinted = null;
      this.onFailed = null;
    },

    /** Reject an interrupted attempt so its caller never records a letter as printed. */
    cancelPrint() {
      this.onFailed?.(new Error('Letter printing was cancelled.'));
      this.resetPrint();
    },

    /** Print only after the letter template and any selected authenticated screenshot are ready. */
    async doPrint() {
      if (this.preview || this.printStarted || !this.onPrinted) {
        return;
      }

      this.printStarted = true;
      const version = this.printVersion;

      try {
        await this.$nextTick();

        if (this.includeScreenshot) {
          await this.$refs.evidenceAttachment.waitUntilReady();
        }

        if (version !== this.printVersion) {
          return;
        }

        const element = this.$el;
        const parent = element.parentElement;
        document.body.appendChild(element);

        try {
          window.print();
        } finally {
          parent.appendChild(element);
        }

        const result = {
          template: this.template,
          options: this.options
        };

        this.onPrinted?.(result);
        this.$emit('printed', result);
      } catch (error) {
        if (version === this.printVersion) {
          this.onFailed?.(error);
        }
      } finally {
        if (version === this.printVersion) {
          this.resetPrint();
        }
      }
    },

    /** Start a bounded print attempt; callers receive failures as well as successful preparation. */
    async print(template, options, includeScreenshot = false) {
      this.cancelPrint();

      const completion = new Promise((resolve, reject) => {
        this.onPrinted = resolve;
        this.onFailed = reject;
      });

      this.template = template;
      this.options = options || {};
      this.includeScreenshot = includeScreenshot;
      this.printTimer = setTimeout(() => this.cancelPrint(), 35000);

      return completion;
    }
  }
}

</script>

<style>
img.seal-logo {
  max-height: 120px;
  max-width: 120px;
  object-fit: contain;
}
img.seal-logo.lg {
  max-width: 240px;
}
@media print {
  body.modal-open {
    overflow: visible !important;
  }

  @page {
    size:auto;
    margin:0.5in 0.75in 0.25in;
  }
}
</style>
